import type { SedeConfig } from './types'
import { ALL_SEDI, DEFAULT_SEDE } from './config'
import { verdeVercelli } from './verde-vercelli'
import { verdeValsesia2 } from './verde-valsesia-2'
import { verdeVercelli2 } from './verde-vercelli-2'

/**
 * DOM-dependent runtime helpers. Separated from ./index.ts so the
 * pure config can be imported from vite.config.ts (Node context) without
 * TypeScript complaining about missing `window`/`document` names.
 */

/** Build a hostname → SedeConfig lookup from all registered sedi. */
const HOSTNAME_MAP: Record<string, SedeConfig> = Object.fromEntries(
  ALL_SEDI.flatMap((sede) => sede.domains.map((d) => [d, sede])),
)

/** Dev-mode: map localhost ports to sedi for easy local testing. */
const DEV_PORT_MAP: Record<string, SedeConfig> = {
  '5174': verdeVercelli,
  '5175': verdeValsesia2, // tunnelled: verdevalsesia-dev.avmg.it → landings:5175
  '5177': verdeVercelli2,
}

/**
 * Resolve the current sede from `window.location.hostname`.
 *
 * Used only in dev fallback — production builds embed VITE_SITE_SLUG
 * at build time and App.tsx short-circuits this lookup.
 *
 * Falls back to DEFAULT_SEDE if:
 *   - Running on localhost during dev without a matching port
 *   - An unknown domain points here (misconfig)
 *   - SSR contexts (no window)
 */
export function getCurrentSede(): SedeConfig {
  if (typeof window === 'undefined') return DEFAULT_SEDE
  const host = window.location.hostname.toLowerCase()

  if (host === 'localhost' || host === '127.0.0.1') {
    const port = window.location.port
    if (DEV_PORT_MAP[port]) return DEV_PORT_MAP[port]
  }

  return HOSTNAME_MAP[host] ?? DEFAULT_SEDE
}

/**
 * Apply a sede's brand colors to the document root via CSS variables.
 * Tailwind utility classes (text-brand, bg-brand, etc.) resolve to these.
 *
 * NOTE: Page title and meta description are baked into index.html at
 * build time (see vite.config.ts → inject-sede-seo plugin). Runtime
 * mutation of those tags used to live here but crawlers don't execute
 * JS, so it was dead weight — removed.
 */
export function applySedeTheme(sede: SedeConfig): void {
  const root = document.documentElement
  root.style.setProperty('--brand', sede.brand.primaryRgb)
  root.style.setProperty('--brand-hover', sede.brand.primaryHoverRgb)
  root.style.setProperty('--brand-ink', sede.brand.inkRgb)
}
